import { FunctionComponent, useCallback, useMemo, useState } from 'react'

import router from 'next/router'
import styled, { css } from 'styled-components'

import config from '@config'
import { mobileCSS } from '@measures/responsive'
import { TQuestionData } from '@utils/chatbot/types'
import translate from '@i18n'
import useAISearchAnswer from '@hooks/useAISearch/useAISearchAnswer'
import useAISearchQuestions from '@hooks/useAISearch/useAISearchQuestions'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import useTracking, { TrackingFnType } from '@hooks/useTracking'

import AISearchInput from './AISearchInput'
import AISearchSources from './AISearchSources'
import AnswerMessage from './AnswerMessage'
import ModalWindow from './ModalWindow'
import QuestionsList from './SuggestedQuestionsList'
import { QuestionType, SourceType } from './types'
import WelcomeMessage from './WelcomeMessage'

const {
  search: { sectionUrl },
} = config

const StyledContainer = styled.div`
  ${({
    theme: {
      spacing: { spacing56, spacing16 },
    },
  }) => css`
    margin-bottom: ${spacing56};

    ${mobileCSS(css`
      margin-top: ${spacing16};
    `)}
  `}
`

type AISearchProps = {
  onQuestionSubmit: (question: string) => void
}

const AISearch: FunctionComponent<AISearchProps> = ({ onQuestionSubmit }) => {
  const [isSourcesModalVisible, setIsSourcesModalVisible] = useState(false)
  const [selectedSource, setSelectedSource] = useState<SourceType | null>(null)
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionType | null>(
    null,
  )

  const onNoResultTrack = useCallback<
    TrackingFnType<{ searchTerm: string | undefined }>
  >(({ extraData }) => {
    const { searchTerm } = extraData
    return {
      event: 'et_no_result',
      search_term: searchTerm?.toLowerCase(),
    }
  }, [])
  const noResultTrack = useTracking(onNoResultTrack)

  const {
    data: questionsData,
    isLoading: questionsIsLoading,
    error: questionsError,
    refetch: questionsRefetch,
  } = useAISearchQuestions()

  const {
    data: answerData,
    isLoading: answerIsLoading,
    error: answerError,
  } = useAISearchAnswer({
    question: selectedQuestion,
    noResultTrack,
  })

  const { questions } = questionsData || {}

  const onSelectQuestionEvent = useCallback<
    TrackingFnType<{ question: TQuestionData }>
  >(
    ({ extraData }) => {
      const questionIndex =
        questionsData?.questions.findIndex(
          (q) => q.id === extraData.question.id,
        ) ?? -1

      return {
        event: 'et_select_question',
        chatbot_entrypoint: 'search_icon_header',
        eventLabel: questionIndex + 1,
      }
    },
    [questionsData],
  )

  const onQuestionSubmitEvent = useCallback<
    TrackingFnType<{ question: QuestionType }>
  >(({ extraData }) => {
    return {
      event: 'et_search_submit',
      search_term: extraData.question.text?.toLowerCase(),
    }
  }, [])

  const onExpandAnswerEvent = useCallback<TrackingFnType>(() => {
    return {
      event: 'et_button_click',
      button_label: 'expand_answer',
    }
  }, [])

  const onSourcesClickEvent = useCallback<TrackingFnType>(() => {
    return {
      event: 'et_button_click',
      button_label: 'sources',
    }
  }, [])

  const onSourceLinkClickEvent = useCallback<TrackingFnType>(() => {
    return {
      event: 'et_button_click',
      button_label: 'inline_source',
    }
  }, [])

  const onSourceClickEvent = useCallback<
    TrackingFnType<{ source: SourceType; isInlineSource: boolean }>
  >(
    ({ extraData: { source, isInlineSource } }) => {
      return {
        event: 'et_result_click',
        button_label: isInlineSource ? 'inline_source' : 'sources',
        eventLabel: source.articleId,
        search_term: selectedQuestion?.text?.toLowerCase(),
      }
    },
    [selectedQuestion?.text],
  )

  const trackOnSelectQuestion = useTracking(onSelectQuestionEvent)
  const trackQuestionSubmit = useTracking(onQuestionSubmitEvent)
  const trackExpandAnswer = useTracking(onExpandAnswerEvent)
  const trackSourcesClick = useTracking(onSourcesClickEvent)
  const trackSourceLinkClick = useTracking(onSourceLinkClickEvent)
  const trackSourceClick = useTracking(onSourceClickEvent)

  const sources = useMemo(() => {
    const sources = Object.values(answerData?.links ?? {})

    if (selectedSource?.articleId) {
      return sources.filter(
        (source) => source?.articleId === selectedSource?.articleId,
      )
    }

    return sources
  }, [answerData, selectedSource?.articleId])

  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  // create a new history entry to reset the page state when the user clicks on a back button
  const pushToHistory = () => {
    router.push(
      {
        pathname: sectionUrl,
        query: { p: 'search-results' },
      },
      undefined,
      { shallow: true },
    )
  }

  const onQuestionClick = (question: TQuestionData) => {
    setSelectedQuestion({ ...question, isSuggestedQuestion: true })
    trackOnSelectQuestion({ question })
    scrollToTop()
    pushToHistory()
  }

  const handleQuestionSubmit = (question: QuestionType) => {
    setSelectedQuestion(question)
    trackQuestionSubmit({ question })
    onQuestionSubmit(question.text)
    scrollToTop()
    pushToHistory()
  }

  const onExpandClick = () => {
    trackExpandAnswer()
  }

  const onSourcesClick = () => {
    setIsSourcesModalVisible(true)
    trackSourcesClick()
  }

  const onSourcesClose = () => {
    setSelectedSource(null)
    setIsSourcesModalVisible(false)
  }

  const onSourceLinkClick = (linkItem: SourceType) => {
    setSelectedSource(linkItem)
    setIsSourcesModalVisible(true)
    trackSourceLinkClick()
  }

  const onSourceClick = (source: SourceType) => {
    trackSourceClick({ source, isInlineSource: !!selectedSource })
  }

  const resetPageState = useCallback(() => {
    setSelectedQuestion(null)
    setSelectedSource(null)
    setIsSourcesModalVisible(false)
    onQuestionSubmit('')
  }, [onQuestionSubmit])

  useExecuteOnClientNavigation(resetPageState)

  return (
    <StyledContainer>
      <AISearchInput
        answerIsLoading={answerIsLoading}
        onQuestionSubmit={handleQuestionSubmit}
      />
      {selectedQuestion ? (
        <AnswerMessage
          question={selectedQuestion}
          answer={answerData}
          answerIsLoading={answerIsLoading}
          answerHasError={!!answerError}
          onExpandClick={onExpandClick}
          onSourcesClick={onSourcesClick}
          onSourceLinkClick={onSourceLinkClick}
        />
      ) : (
        <>
          <WelcomeMessage />
          <QuestionsList
            questions={questions}
            isLoading={questionsIsLoading}
            hasError={!!questionsError}
            onRefetch={questionsRefetch}
            onQuestionClick={onQuestionClick}
          />
        </>
      )}
      {isSourcesModalVisible && (
        <ModalWindow
          title={translate('aiSearch.answerMessage.sources')}
          onClose={onSourcesClose}
        >
          <AISearchSources sources={sources} onSourceClick={onSourceClick} />
        </ModalWindow>
      )}
    </StyledContainer>
  )
}

export default AISearch
